import { InputNumber, ColorPicker, Switch, Typography, Empty, Divider, Space, Tabs, Descriptions, Slider, Form, Button, List, Select, message , Tooltip} from 'antd';
import { useCallback, useRef, useMemo, useEffect, useState, useReducer } from 'react';
import useEditorUiStore from '../../stores/editorUiStore';
import { useHoverStore } from '../../stores/hoverStore';
import { LeftOutlined, RightOutlined, DeleteOutlined, QuestionCircleOutlined, ClusterOutlined } from '@ant-design/icons';
import type { Model3D, SubObjectNode } from '@dtwin/shared';
import useSceneStore from '../../stores/sceneStore';
import useGisStore from '../../stores/gisStore';
import useSelectionStore from '../../stores/selectionStore';
import usePropertyPanel from '../../hooks/usePropertyPanel';
import { modelRefsRegistry } from '../../stores/modelRefsRegistry';
import { useSceneBindingStore } from '../../stores/sceneBindingStore';
import type { SceneBindingRecord } from '../../api/bindings';
import { useDeviceDataStore } from '../../stores/deviceDataStore';
import { fetchIotDevices } from '../../api/iot';
import { fetchRuleOutputs, ruleOutputDeviceId, ruleOutputPoints } from '../../api/ruleOutputs';
import { VISUAL_TYPES, VISUAL_TYPE_LABEL } from '../../core/visualRule';
import { updateModel as apiUpdateModel } from '../../api/models';
import PivotFixCard from './PivotFixCard';
import { deleteModelsByIds } from '../../utils/modelActions';
import AnimationPanel from './AnimationPanel';
import { findSameMaterialMeshUuids } from './subObjectUtils';
import * as THREE from 'three';

const { Title, Text } = Typography;

// Learning from Three.js editor: radian ↔ degree conversion
const toDegrees = (rad: number) => rad * (180 / Math.PI);
const toRadians = (deg: number) => deg * (Math.PI / 180);

// Debounce helper to avoid excessive history snapshots, also syncs to backend
function useDebouncedUpdate() {
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const { updateModel } = useSceneStore();
  
  const debouncedUpdate = useCallback((id: string, updates: Record<string, any>) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      updateModel(id, updates, false);
      apiUpdateModel(id, updates).catch(() => {});
    }, 100);
  }, [updateModel]);
  
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  
  return debouncedUpdate;
}

// Learning from Three.js editor: Geometry info tab
function GeometryInfo({ model }: { model: any }) {
  const handle = modelRefsRegistry.get(model.id);
  
  const geometryInfo = useMemo(() => {
    if (!handle?.group) return null;
    
    const box = new THREE.Box3().setFromObject(handle.group);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);
    
    let vertexCount = 0;
    let triangleCount = 0;
    handle.group.traverse((child: any) => {
      if (child instanceof THREE.Mesh) {
        vertexCount += child.geometry.attributes.position?.count ?? 0;
        triangleCount += child.geometry.index 
          ? child.geometry.index.count / 3 
          : (child.geometry.attributes.position?.count ?? 0) / 3;
      }
    });
    
    return { vertexCount, triangleCount, size, center };
  }, [handle]);
  
  if (!geometryInfo) return <div style={{ padding: 8, color: '#888' }}>模型未加载</div>;
  
  return (
    <Descriptions column={1} size="small" style={{ fontSize: 12 }}>
      <Descriptions.Item label="顶点数">{geometryInfo.vertexCount.toLocaleString()}</Descriptions.Item>
      <Descriptions.Item label="三角面数">{geometryInfo.triangleCount.toLocaleString()}</Descriptions.Item>
      <Descriptions.Item label="包围盒">
        {geometryInfo.size.x.toFixed(2)} × {geometryInfo.size.y.toFixed(2)} × {geometryInfo.size.z.toFixed(2)}
      </Descriptions.Item>
      <Descriptions.Item label="中心点">
        ({geometryInfo.center.x.toFixed(2)}, {geometryInfo.center.y.toFixed(2)}, {geometryInfo.center.z.toFixed(2)})
      </Descriptions.Item>
    </Descriptions>
  );
}

// Learning from Three.js editor: Material editor tab
// 2026-09-08｜阶段4：支持选中子构件时编辑其材质——GLB 共享材质坑：修改前 clone 独立副本，不污染其他 mesh
function MaterialEditor({ model }: { model: any }) {
  const { updateModel } = useSceneStore();
  const selectedSubObject = useSceneStore((s) => s.selectedSubObject);
  const handle = modelRefsRegistry.get(model.id);
  const isSubTarget = selectedSubObject?.modelId === model.id;
  // 2026-09-27（审计 FE-B09 修复）：双面渲染开关受控于 three 材质属性（非 React state），
  // onChange 后不触发重渲染 → 开关状态不刷新。用 forceUpdate 回显。
  const [, forceMatUpdate] = useReducer((x: number) => x + 1, 0);

  // 2026-09-08｜修复：FBX 导入材质常为 Phong/Lambert（非 Standard）——放宽为任意可编辑 Mesh 材质
// 颜色/透明度/双面通用；金属度/粗糙度仅属性存在时显示编辑
function isEditableMaterial(m: any): boolean {
  return !!m && (m.isMeshStandardMaterial || m.isMeshPhongMaterial || m.isMeshLambertMaterial || m.isMeshBasicMaterial || m.isMaterial);
}

// 目标对象：子构件选中 → 该 uuid 节点（Group 自动取其第一个含可编辑材质的后代 Mesh）；否则顶层模型（旧逻辑）
  const targetMesh: THREE.Mesh | null = useMemo(() => {
    if (!handle?.group) return null;
    if (isSubTarget) {
      const obj = handle.group.getObjectByProperty('uuid', selectedSubObject!.uuid);
      if (!obj) return null;
      if ((obj as any).isMesh) return obj as THREE.Mesh;
      // 分组构件：取第一个含可编辑材质的后代网格（可直接编辑，提示实际对象）
      let found: THREE.Mesh | null = null;
      obj.traverse((child: any) => {
        if (!found && child instanceof THREE.Mesh && isEditableMaterial(child.material)) found = child;
      });
      return found;
    }
    let found: THREE.Mesh | null = null;
    handle.group.traverse((child: any) => {
      if (child instanceof THREE.Mesh && isEditableMaterial(child.material)) {
        found = child;
      }
    });
    return found;
  }, [handle, isSubTarget, selectedSubObject]);

  const material: THREE.Material | null = useMemo(() => {
    if (!targetMesh) return null;
    const mats = Array.isArray(targetMesh.material) ? targetMesh.material : [targetMesh.material];
    let std = mats.find((m: any) => isEditableMaterial(m)) as THREE.Material | undefined;
    if (std && isSubTarget && !(std as any).__dtwCloned) {
      // GLB 共享材质坑：多个 mesh 共用同一材质实例 → clone 独立副本，仅当前构件生效
      const cloned = std.clone();
      (cloned as any).__dtwCloned = true;
      targetMesh.material = cloned;
      std = cloned;
    }
    return std ?? null;
  }, [targetMesh, isSubTarget]);

  if (!material) return (
    <div style={{ padding: 8, color: '#888' }}>
      {isSubTarget ? '该构件无网格/可编辑材质，可选中其子网格编辑' : '无材质'}
    </div>
  );

  const mat = material as any;

  const syncMaterials = (key: string, value: any) => {
    (mat as any)[key] = value;
    mat.needsUpdate = true;
    if (key === 'opacity' && value < 1) {
      mat.transparent = true;
    }
    // 子构件材质：写入构件配置（阶段5 随工程保存）；顶层逻辑不变
    if (isSubTarget && targetMesh) {
      const hexColor = '#' + mat.color.getHexString();
      useSceneStore.getState().updateSubObjectConfig(model.id, {
        uuid: targetMesh.uuid,
        name: targetMesh.name || selectedSubObject?.uuid,
        material: { color: hexColor, opacity: mat.opacity, metalness: mat.metalness, roughness: mat.roughness },
      });
      return;
    }
    const hexColor = '#' + mat.color.getHexString();
    const materialsConfig = [{
      name: mat.name || 'default',
      color: hexColor,
      opacity: mat.opacity,
      metalness: mat.metalness,
      roughness: mat.roughness,
    }];
    updateModel(model.id, { materials: materialsConfig }, false);
    apiUpdateModel(model.id, { materials: materialsConfig }).catch(() => {});
  };

  return (
    <>
      {isSubTarget && (
        <div style={{ marginBottom: 8, fontSize: 11, color: '#1677ff', background: 'rgba(22,119,255,0.1)', padding: '4px 8px', borderRadius: 4 }}>
          正在编辑构件：{targetMesh?.name || selectedSubObject?.uuid}
          {targetMesh && targetMesh.uuid !== selectedSubObject?.uuid && (
            <Text type="secondary" style={{ fontSize: 10 }}>（分组下第 1 个可编辑网格）</Text>
          )}
        </div>
      )}
      <Form layout="vertical" size="small">
      {'color' in mat && (
        <Form.Item label="颜色">
          <input 
            type="color" 
            value={`#${mat.color.getHexString()}`}
            onChange={(e) => syncMaterials('color', new THREE.Color(e.target.value))}
            style={{ width: '100%', height: 32, border: 'none', cursor: 'pointer' }}
          />
        </Form.Item>
      )}
      {'metalness' in mat && (
        <Form.Item label="金属度">
          <Slider 
            min={0} max={1} step={0.01} 
            value={mat.metalness}
            onChange={(v) => syncMaterials('metalness', v)}
          />
        </Form.Item>
      )}
      {'roughness' in mat && (
        <Form.Item label="粗糙度">
          <Slider 
            min={0} max={1} step={0.01} 
            value={mat.roughness}
            onChange={(v) => syncMaterials('roughness', v)}
          />
        </Form.Item>
      )}
      <Form.Item label="透明度">
        <Slider 
          min={0} max={1} step={0.01} 
          value={mat.opacity}
          onChange={(v) => syncMaterials('opacity', v)}
        />
      </Form.Item>
      <Form.Item label="双面渲染">
        <Switch 
          checked={mat.side === THREE.DoubleSide}
          onChange={(v) => {
            mat.side = v ? THREE.DoubleSide : THREE.FrontSide;
            mat.needsUpdate = true;
            forceMatUpdate();
          }}
        />
      </Form.Item>
    </Form>
    </>
  );
}

/** 多选批量属性面板：多选（Ctrl/Shift 点击、框选、Ctrl+A 全选）时右侧属性同步显示选中集合，
 *  支持批量可见性 / 统一缩放 / 聚焦单个 / 批量删除（与主流编辑器一致） */
function MultiSelectPanel({ ids, models }: { ids: string[]; models: any[] }) {
  const updateModel = useSceneStore((s) => s.updateModel);
  const selectModel = useSelectionStore((s) => s.select);
  const allVisible = models.every((m) => m.visible);

  const batchScale = (axis: number, v: number | null) => {
    if (v == null || v <= 0) return;
    models.forEach((m) => {
      const scale: [number, number, number] = [m.scale[0], m.scale[1], m.scale[2]];
      scale[axis] = v;
      updateModel(m.id, { scale });
    });
  };

  return (
    <div style={{ padding: 16 }}>
      <Title level={5} style={{ marginBottom: 4 }}>已选 {ids.length} 个模型</Title>
      <Text style={{ fontSize: 12, color: '#888' }}>
        点击列表项可聚焦单个模型 · Ctrl/Shift+点击或框选可调整范围
      </Text>

      <Divider style={{ margin: '12px 0' }} />

      <Space orientation="vertical" style={{ width: '100%' }} size="middle">
        {/* 批量可见性 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text style={{ fontSize: 12, color: '#888' }}>可见（全部）</Text>
          <Switch
            checked={allVisible}
            onChange={(v) => models.forEach((m) => updateModel(m.id, { visible: v }))}
          />
        </div>

        {/* 统一缩放 */}
        <div>
          <Text style={{ fontSize: 12, color: '#888' }}>缩放（统一设置全部）</Text>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 4 }}>
            {(['X', 'Y', 'Z'] as const).map((axisLabel, i) => (
              <InputNumber
                key={axisLabel}
                size="small"
                addonBefore={axisLabel}
                style={{ width: '100%' }}
                value={models[0]?.scale[i]}
                step={0.1}
                min={0.001}
                onChange={(v) => batchScale(i, v)}
              />
            ))}
          </div>
        </div>

        {/* 选中对象列表（点击聚焦） */}
        <div>
          <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>选中对象</Text>
          <List
            size="small"
            dataSource={models}
            locale={{ emptyText: '无选中模型' }}
            renderItem={(m: any) => (
              <List.Item
                onClick={() => selectModel(m.id)}
                style={{ cursor: 'pointer', padding: '6px 0' }}
                actions={[
                  <Text key="state" style={{ fontSize: 11, color: m.visible ? '#52c41a' : '#888' }}>
                    {m.visible ? '可见' : '隐藏'}
                  </Text>,
                ]}
              >
                <Text style={{ fontSize: 12 }}>{m.name}</Text>
              </List.Item>
            )}
          />
        </div>
      </Space>

      <Divider style={{ margin: '12px 0' }} />

      <Button danger block icon={<DeleteOutlined />} onClick={() => deleteModelsByIds(ids)}>
        删除所选 {ids.length} 个模型
      </Button>
    </div>
  );
}

/** 阶段4｜G3 设备联动选择器：模型 ↔ 设备绑定（注册表下拉+手动输入回退）。
 *  选择即持久化（create/update/delete device-link 绑定）+ 同步内存 model.deviceId（3D 联动立即生效）。 */
function DeviceLinkSelect({ model }: { model: Model3D }) {
  const { updateModel } = useSceneStore();
  const currentScene = useSceneStore((s) => s.currentScene);
  const bindings = useSceneBindingStore((s) => s.bindings);
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [busy, setBusy] = useState(false);

  const linkBinding = bindings.find(
    (b) => b.modelNodeId === model.id && b.visualType === 'device-link',
  );
  const labelEnabled = (linkBinding?.ruleConfig as any)?.label === true;

  // 阶段6｜悬浮标签开关：写 device-link ruleConfig.label（持久化）
  const toggleLabel = async (v: boolean) => {
    if (!currentScene?.id || !linkBinding) return;
    const ok = await useSceneBindingStore.getState().updateBindingRule(currentScene.id, linkBinding.id, { label: v });
    if (!ok) message.error('悬浮标签设置失败');
  };

  useEffect(() => {
    fetchIotDevices()
      .then((list) =>
        setOptions(
          list.map((d) => ({
            value: d.deviceId,
            label: `${d.name}${d.imei && d.imei !== d.name ? ` (${d.imei})` : ''}`,
          })),
        ),
      )
      .catch(() => {});
  }, []);

  const apply = async (deviceId: string | null) => {
    const sceneId = currentScene?.id;
    if (!sceneId || busy) return;
    setBusy(true);
    try {
      const saved = await useSceneBindingStore
        .getState()
        .setDeviceBinding(sceneId, model.id, model.name, deviceId);
      if (saved) {
        updateModel(model.id, { deviceId: deviceId || undefined });
      } else {
        message.error('设备绑定保存失败');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Select
      style={{ width: '100%' }}
      size="small"
      placeholder="选择设备（绑定后 3D 点击/设备表格可联动）"
      loading={busy}
      showSearch
      allowClear
      value={model.deviceId ?? undefined}
      onChange={(v) => apply(v ?? null)}
      filterOption={(input: string, option: any) =>
        [option?.label, option?.value].filter(Boolean).some((s) => String(s).toLowerCase().includes(input.toLowerCase()))
      }
      options={options}
      notFoundContent="暂无设备：请到「插件 → RTU 设备管理」添加/绑定，或手动输入"
      dropdownRender={(menu) => (
        <>
          {menu}
          <InputNumber
            style={{ margin: 4, width: 'calc(100% - 8px)' }}
            size="small"
            placeholder="或手动输入设备ID / IMEI"
            onPressEnter={(e: any) => {
              const v = String(e.target.value).trim();
              if (v) apply(v);
            }}
          />
        </>
      )}
    />
  );
}

function RuleOutputBindingPanel({ model }: { model: Model3D }) {
  const currentScene = useSceneStore((s) => s.currentScene);
  const [outputs, setOutputs] = useState<{ value: string; label: string }[]>([]);
  const [key, setKey] = useState<string>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchRuleOutputs().then((list) => {
      for (const o of list) {
        useDeviceDataStore.getState().upsertDevicePoints(
          ruleOutputDeviceId(o.outputKey),
          ruleOutputPoints(o.outputKey, o.value),
          { online: true, timestamp: new Date(o.ts).getTime() || Date.now() },
        );
      }
      setOutputs(list.map((o) => ({ value: o.outputKey, label: `${o.outputKey}（${o.valueType}）` })));
    }).catch(() => undefined);
  }, []);

  const bind = async (visualType: 'color' | 'height' | 'rotationSpeed') => {
    if (!currentScene?.id || !key || busy) return;
    setBusy(true);
    const ruleConfig = visualType === 'color'
      ? { ranges: [{ min: 0, max: 1, color: '#2ecc71' }, { min: 1, max: 2, color: '#e74c3c' }] }
      : visualType === 'height'
        ? { height: { min: 0, max: 100 } }
        : { rotationSpeed: { maxRps: 1, axis: 'y' } };
    const ok = await useSceneBindingStore.getState().setVisualBinding(
      currentScene.id, model.id, model.name, visualType,
      { deviceId: ruleOutputDeviceId(key), bindPointKey: 'value', ruleConfig },
    );
    setBusy(false);
    if (ok) message.success('规则链输出已绑定');
    else message.error('绑定保存失败');
  };

  return (
    <div style={{ border: '1px dashed #5a4a80', borderRadius: 6, padding: '8px 10px', background: '#171426' }} data-testid="rule-output-binding-panel">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <Text style={{ fontSize: 12, color: '#b8a8e8' }}>规则链输出</Text>
        <Text type="secondary" style={{ fontSize: 10 }}>绑定虚拟设备 rule:{key || '{key}'}</Text>
      </div>
      <Select
        size="small"
        style={{ width: '100%' }}
        showSearch
        allowClear
        placeholder={outputs.length ? '选择规则链输出' : '暂无规则链输出（先在规则链添加 Output 节点）'}
        value={key}
        options={outputs}
        onChange={setKey}
        filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        data-testid="rule-output-select"
      />
      <Space style={{ marginTop: 6 }} wrap>
        <Button size="small" disabled={!key || busy} onClick={() => bind('color')}>绑定颜色</Button>
        <Button size="small" disabled={!key || busy} onClick={() => bind('height')}>绑定高度</Button>
        <Button size="small" disabled={!key || busy} onClick={() => bind('rotationSpeed')}>绑定旋转</Button>
      </Space>
    </div>
  );
}

/** 阶段6｜悬浮标签开关（设备联动绑定已存在时可用） */
function DeviceLabelToggle({ model }: { model: Model3D }) {
  const bindings = useSceneBindingStore((s) => s.bindings);
  const linkBinding = bindings.find((b) => b.modelNodeId === model.id && b.visualType === 'device-link');
  if (!linkBinding) return null;
  const enabled = (linkBinding.ruleConfig as any)?.label === true;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
      <Text style={{ fontSize: 12, color: '#888' }}>悬浮标签（实时点位/告警）</Text>
      <Switch
        size="small"
        checked={enabled}
        onChange={(v) => {
          useSceneBindingStore
            .getState()
            .updateBindingRule(linkBinding.sceneId, linkBinding.id, { label: v })
            .then((ok) => { if (!ok) message.error('悬浮标签设置失败'); });
        }}
      />
    </div>
  );
}

/** 阶段5｜绑定配置 UI：模型 ↔ 设备点位 → 视觉规则（color 阈值-颜色区间）
 *  列表展示已配置绑定；新增/编辑：视觉类型 → 设备下拉 → 点位下拉（随设备实时点位）→ 规则区间编辑器 */
function VisualBindingPanel({ model }: { model: Model3D }) {
  const currentScene = useSceneStore((s) => s.currentScene);
  const bindings = useSceneBindingStore((s) => s.bindings);
  const deviceMap = useDeviceDataStore((s) => s.deviceMap);
  const subObjects = useSceneStore((s) => s.subObjectsMap?.[model.id]);
  const [devices, setDevices] = useState<{ value: string; label: string }[]>([]);
  const [draft, setDraft] = useState<{
    visualType: string; deviceId: string; bindPointKey: string;
    subObjectUuid?: string;
    ranges: { min: number | null; max: number | null; color: string }[];
    height?: { min: number | null; max: number | null; tankH: number | null };
    rps?: { maxRps: number | null; axis: string };
    act?: { name: string; threshold: number | null };
  } | null>(null);
  const [busy, setBusy] = useState(false);

  // 2026-09-08｜阶段4：构件选择（组合模型内部节点，uuid 定位；空=整模型）
  const subOptions = useMemo(() => {
    const out: { value: string; label: string }[] = [];
    const walk = (nodes: SubObjectNode[], prefix = '') => {
      nodes.forEach((n) => {
        out.push({ value: n.uuid, label: `${prefix}${n.name}` });
        walk(n.children, `${prefix}${n.name} / `);
      });
    };
    if (subObjects) walk(subObjects);
    return out;
  }, [subObjects]);
  const subNameOf = (uuid?: string) => {
    if (!uuid || !subObjects) return '';
    let found = '';
    const walk = (nodes: SubObjectNode[]) => {
      nodes.forEach((n) => {
        if (n.uuid === uuid) found = n.name;
        walk(n.children);
      });
    };
    walk(subObjects);
    return found;
  };

  // 骨骼动画名（GLB useAnimations）
  const animNames = (modelRefsRegistry.get(model.id)?.animations?.names ?? []) as string[];

  const sceneId = currentScene?.id;
  const mine = bindings.filter((b) => b.modelNodeId === model.id);

  useEffect(() => {
    fetchIotDevices()
      .then((list) =>
        setDevices(
          list.map((d) => ({
            value: d.deviceId,
            label: `${d.name}${d.imei && d.imei !== d.name ? ` (${d.imei})` : ''}`,
          })),
        ),
      )
      .catch(() => {});
  }, []);

  // 选中设备的实时点位 keys（如 io.ai0 / network.cellular.rssiDbm）
  // 2026-09-30：按 key 排序（io.ai0 等业务点位排前）；下拉为虚拟滚动，仅渲染首屏 ~10 项，
  // 不排序时实时点位居后（如 io.ai0 排第 13），用户"看不到点位"。
  const draftPointKeys = draft
    ? Object.keys(deviceMap[draft.deviceId]?.points ?? {}).sort((a, b) => a.localeCompare(b))
    : [];

  const saveDraft = async () => {
    if (!sceneId || !draft) return;
    setBusy(true);
    const rules: Record<string, unknown> = {};
    if (draft.visualType === 'color') {
      rules.ranges = draft.ranges
        .filter((r) => r.min != null && r.max != null && r.color)
        .map((r) => ({ min: Number(r.min), max: Number(r.max), color: r.color }));
    } else if (draft.visualType === 'height') {
      rules.height = {
        min: draft.height?.min != null ? Number(draft.height.min) : 0,
        max: draft.height?.max != null ? Number(draft.height.max) : 100,
        tankH: draft.height?.tankH != null ? Number(draft.height.tankH) : undefined,
      };
    } else if (draft.visualType === 'rotationSpeed') {
      rules.rotationSpeed = {
        maxRps: draft.rps?.maxRps != null ? Number(draft.rps.maxRps) : 0.5,
        axis: draft.rps?.axis ?? 'y',
      };
    } else if (draft.visualType === 'action') {
      rules.action = {
        name: draft.act?.name ?? '',
        threshold: draft.act?.threshold != null ? Number(draft.act.threshold) : 0.5,
      };
    }
    const ok = await useSceneBindingStore
      .getState()
      .setVisualBinding(sceneId, model.id, model.name, draft.visualType, {
        deviceId: draft.deviceId,
        bindPointKey: draft.bindPointKey,
        ruleConfig: { ...rules, subObjectUuid: draft.subObjectUuid ?? '', subName: draft.subObjectUuid ? subNameOf(draft.subObjectUuid) : '' },
      });
    setBusy(false);
    if (ok) setDraft(null);
    else message.error('绑定保存失败');
  };

  const removeBinding = async (b: SceneBindingRecord) => {
    if (!sceneId) return;
    const ok = await useSceneBindingStore
      .getState()
      .setVisualBinding(sceneId, model.id, model.name, b.visualType, { deviceId: '', bindPointKey: '' });
    if (!ok) message.error('删除绑定失败');
  };

  return (
    <div style={{ border: '1px dashed #3d5a80', borderRadius: 6, padding: '8px 10px', background: '#14141f' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <Text style={{ fontSize: 12, color: '#888' }}>数据绑定（视觉规则）</Text>
        <Button
          size="small" type="text"
          onClick={() =>
            setDraft({ visualType: 'color', deviceId: devices[0]?.value ?? '', bindPointKey: '', ranges: [{ min: 0, max: 100, color: '#1890ff' }] })
          }
        >
          + 新增绑定
        </Button>
      </div>

      {mine.filter((b) => b.visualType !== 'device-link').map((b) => (
        <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <Text style={{ fontSize: 11, color: '#e0e0e0', flex: 1 }}>
            {VISUAL_TYPE_LABEL[b.visualType] || b.visualType} · {b.deviceId} / {b.bindPointKey}
            {(b.ruleConfig as any)?.subName ? <Text type="secondary" style={{ fontSize: 10 }}> → {String((b.ruleConfig as any).subName)}</Text> : null}
          </Text>
          <Button size="small" type="text" danger onClick={() => removeBinding(b)}>删除</Button>
        </div>
      ))}
      {mine.filter((b) => b.visualType !== 'device-link').length === 0 && !draft && (
        <Text style={{ fontSize: 11, color: '#555', display: 'block' }}>
          未配置视觉绑定 — 「+ 新增绑定」后设备点位数值将实时驱动模型颜色
        </Text>
      )}

      {draft && (
        <Space orientation="vertical" style={{ width: '100%', marginTop: 4 }} size={6}>
          <Select
            size="small" style={{ width: '100%' }}
            value={draft.visualType}
            options={VISUAL_TYPES as unknown as { value: string; label: string }[]}
            onChange={(v) => setDraft({ ...draft, visualType: v })}
          />
          {/* 2026-09-08｜阶段4：构件选择（组合模型内部节点；不选=整模型，兼容旧行为） */}
          {subOptions.length > 0 && (
            <Select
              size="small" style={{ width: '100%' }} showSearch allowClear placeholder="作用构件（可选，组合模型内部节点）"
              value={draft.subObjectUuid}
              options={subOptions}
              filterOption={(input: string, option: any) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              onChange={(v) => setDraft({ ...draft, subObjectUuid: v })}
            />
          )}
          <Select
            size="small" style={{ width: '100%' }} showSearch placeholder="设备"
            value={draft.deviceId || undefined}
            options={devices}
            filterOption={(input: string, option: any) =>
              [option?.label, option?.value].filter(Boolean).some((s) => String(s).toLowerCase().includes(input.toLowerCase()))
            }
            onChange={(v) => setDraft({ ...draft, deviceId: v, bindPointKey: '' })}
          />
          <Select
            size="small" style={{ width: '100%' }} showSearch placeholder="点位 key（如 io.ai0，来自实时点位）"
            value={draft.bindPointKey || undefined}
            options={draftPointKeys.map((k) => ({ value: k, label: k }))}
            listHeight={320}
            optionFilterProp="value"
            filterOption={(input: string, option: any) => String(option?.value ?? '').toLowerCase().includes(input.toLowerCase())}
            onChange={(v) => setDraft({ ...draft, bindPointKey: v })}
            notFoundContent={draftPointKeys.length ? '无匹配点位（可输入关键字过滤）' : '该设备暂无实时点位（等待 /ws/iot 推送）'}
          />
          {draft.visualType === 'color' && (
            <div>
              <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>阈值-颜色区间</Text>
              {draft.ranges.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
                  <InputNumber size="small" style={{ width: 74 }} placeholder="min" value={r.min} onChange={(v) => {
                    const next = [...draft.ranges]; next[i] = { ...next[i], min: v }; setDraft({ ...draft, ranges: next });
                  }} />
                  <Text style={{ color: '#555', fontSize: 11 }}>~</Text>
                  <InputNumber size="small" style={{ width: 74 }} placeholder="max" value={r.max} onChange={(v) => {
                    const next = [...draft.ranges]; next[i] = { ...next[i], max: v }; setDraft({ ...draft, ranges: next });
                  }} />
                  <ColorPicker size="small" value={r.color} onChange={(c) => {
                    const next = [...draft.ranges]; next[i] = { ...next[i], color: c.toHexString() }; setDraft({ ...draft, ranges: next });
                  }} />
                  <Button size="small" type="text" danger onClick={() => setDraft({ ...draft, ranges: draft.ranges.filter((_, j) => j !== i) })}>×</Button>
                </div>
              ))}
              <Button size="small" type="dashed" style={{ width: '100%' }} onClick={() => setDraft({ ...draft, ranges: [...draft.ranges, { min: null, max: null, color: '#52c41a' }] })}>
                + 增加区间
              </Button>
            </div>
          )}
          {draft.visualType === 'height' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
              <InputNumber size="small" addonBefore="min" placeholder="点位下限" value={draft.height?.min ?? 0}
                onChange={(v) => setDraft({ ...draft, height: { min: v, max: draft.height?.max ?? 100, tankH: draft.height?.tankH ?? 3 } })} />
              <InputNumber size="small" addonBefore="max" placeholder="点位上限" value={draft.height?.max ?? 100}
                onChange={(v) => setDraft({ ...draft, height: { min: draft.height?.min ?? 0, max: v, tankH: draft.height?.tankH ?? 3 } })} />
              <InputNumber size="small" addonBefore="罐高" placeholder="罐体高度" value={draft.height?.tankH ?? 3}
                onChange={(v) => setDraft({ ...draft, height: { min: draft.height?.min ?? 0, max: draft.height?.max ?? 100, tankH: v } })} />
            </div>
          )}
          {draft.visualType === 'rotationSpeed' && (
            <div style={{ display: 'flex', gap: 4 }}>
              <Select size="small" style={{ width: 90 }} value={draft.rps?.axis ?? 'y'} options={[
                { value: 'y', label: 'Y轴(风机)' }, { value: 'x', label: 'X轴' }, { value: 'z', label: 'Z轴' },
              ]} onChange={(axis) => setDraft({ ...draft, rps: { maxRps: draft.rps?.maxRps ?? 0.5, axis } })} />
              <InputNumber size="small" addonBefore="满值转速(圈/s)" step={0.1} value={draft.rps?.maxRps ?? 0.5}
                onChange={(v) => setDraft({ ...draft, rps: { maxRps: v, axis: draft.rps?.axis ?? 'y' } })} />
            </div>
          )}
          {draft.visualType === 'action' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Select
                size="small" style={{ width: '100%' }} placeholder="骨骼动画名（GLB 内嵌动作）"
                value={draft.act?.name || undefined}
                options={animNames.map((n) => ({ value: n, label: n }))}
                onChange={(name) => setDraft({ ...draft, act: { name, threshold: draft.act?.threshold ?? 0.5 } })}
                notFoundContent={animNames.length ? undefined : '当前模型无骨骼动画（GLB 内嵌动作）'}
              />
              <InputNumber size="small" addonBefore="触发阈值" step={0.1} value={draft.act?.threshold ?? 0.5}
                onChange={(v) => setDraft({ ...draft, act: { name: draft.act?.name ?? '', threshold: v } })} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            <Button size="small" type="primary" loading={busy} onClick={saveDraft}>保存绑定</Button>
            <Button size="small" onClick={() => setDraft(null)}>取消</Button>
          </div>
          <Text style={{ fontSize: 10, color: '#555', display: 'block' }}>
            保存后设备实时点位将按区间映射模型颜色（左侧 3D 即时预览）
          </Text>
        </Space>
      )}
    </div>
  );
}

export default function PropertyPanel() {
  const { models, selectedModelId, updateModel, selectModel } = useSceneStore();
  // 2026-09-14：选中同源部件（同共享材质）——计算列表用于按钮（数量展示 + 一键多选）
  const subSel = useSceneStore((s) => s.selectedSubObject);
  const sameMaterialUuids = useMemo(() => {
    if (!subSel || subSel.modelId !== selectedModelId) return [];
    const h = modelRefsRegistry.get(subSel.modelId);
    if (!h?.group) return [];
    try {
      return findSameMaterialMeshUuids(h.group, subSel.uuid);
    } catch {
      return [];
    }
  }, [subSel, selectedModelId]);
  // 2026-09-14：拖拽步长 + 拖拽幅度（订阅以保证输入回显）
  const moveSnap = useEditorUiStore((s) => s.moveSnap);
  const rotateSnap = useEditorUiStore((s) => s.rotateSnap);
  const dragScale = useEditorUiStore((s) => s.dragScale);
  const hoverHighlight = useEditorUiStore((s) => s.hoverHighlight);
  const gisEnabled = useGisStore((s) => s.gisEnabled);
  const selectedIds = useSelectionStore((s) => s.selectedIds);
  const selectedModel = models.find((m) => m.id === selectedModelId);
  const animHandle = selectedModelId ? modelRefsRegistry.get(selectedModelId) : null;
  const debouncedUpdate = useDebouncedUpdate();
  const { activePanel, goBack, goForward, canGoBack, canGoForward } = usePropertyPanel();

  const logUpdate = useCallback((id: string, updates: Record<string, any>) => {
    console.log(`[PropertyPanel] updateModel called for ${id}:`, JSON.stringify(updates));
    debouncedUpdate(id, updates);
  }, [debouncedUpdate]);

  // 多选：右侧属性同步显示"已选 N 个模型"批量面板（3D 高亮与属性页保持一致）
  const selectedMultiModels = selectedIds.length > 1
    ? selectedIds.map((id) => models.find((m) => m.id === id)).filter((m) => m !== undefined)
    : [];
  // 2026-10-15（P0-③）：右侧顶部面包屑「场景 > 模型 > xx」；点「场景」清空选中并回到场景属性面板
  const breadcrumb = (
    <div
      data-testid="prop-breadcrumb"
      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 16px', borderBottom: '1px solid #30475e', background: '#1a1a2e', fontSize: 12 }}
    >
      <a
        data-testid="bc-scene"
        onClick={() => { selectModel(null); useEditorUiStore.getState().setActivePanel('scene'); }}
        style={{ color: '#40a9ff', cursor: 'pointer' }}
      >
        场景
      </a>
      {selectedModel && (
        <>
          <span style={{ color: '#555' }}>/</span>
          <span style={{ color: '#888' }}>模型</span>
          <span style={{ color: '#555' }}>/</span>
          <span data-testid="bc-current" style={{ color: '#e0e0e0' }}>{selectedModel.name}</span>
        </>
      )}
    </div>
  );
  if (selectedMultiModels.length > 1) {
    return <>{breadcrumb}<MultiSelectPanel ids={selectedMultiModels.map((m) => m.id)} models={selectedMultiModels} /></>;
  }

  if (!selectedModel) {
    return (
      <>
        {breadcrumb}
        <div style={{ padding: 24, minHeight: '100%', background: '#1a1a2e' }}>
          <Empty description="选中模型后可编辑属性" />
        </div>
      </>
    );
  }

  return (
    <>
      {breadcrumb}
      <div style={{ padding: 16, minHeight: '100%', background: '#1a1a2e' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>属性面板</Title>
        <Space>
          <Button
            type="text"
            size="small"
            icon={<LeftOutlined />}
            onClick={goBack}
            disabled={!canGoBack()}
          />
          <Button
            type="text"
            size="small"
            icon={<RightOutlined />}
            onClick={goForward}
            disabled={!canGoForward()}
          />
        </Space>
      </div>
      <Text strong style={{ fontSize: 13 }}>{selectedModel.name}</Text>

      <Divider style={{ margin: '12px 0' }} />

      <Tabs 
        size="small" 
        activeKey={activePanel || 'object'}
        onChange={(key) => usePropertyPanel.getState().setActivePanel(key)}
        items={[
          {
            key: 'object',
            label: '对象',
            children: (
              <Space orientation="vertical" style={{ width: '100%' }} size="middle">
                {/* 2026-10-07：轴心修正（第三方模型视觉中心≠原点时一键修正/手动微调） */}
                <PivotFixCard model={selectedModel} />
                {/* 2026-09-14：拖拽步长设置（移动/旋转吸附；默认 0=连续拖动） */}
                <div>
                  <Text style={{ fontSize: 12, color: '#888' }}>
                    拖拽步长{' '}
                    <Tooltip
                      title={
                        <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                          如何设置（拖拽手感）：<br />
                          · <b>拖拽幅度</b>：拖动灵敏度系数。1 = 正常；<b>0.3~0.5 = 微调</b>（放大模型/部件后拖动更精细，推荐）；<b>0.01~0.1 = 极精细</b>（毫米级微调）；2 = 粗调。<b>同时作用于鼠标左键旋转/平移相机的灵敏度</b>。<br />
                          · 移动(m)：按该米数吸附网格。0 = 连续拖动不吸附；设 0.5 每次移动对齐 0.5 米。<br />
                          · 旋转(°)：按该角度吸附。0 = 自由旋转；设 15 每次旋转 15° 对齐。<br />
                          · 设置保存在本机（刷新后保留），可随时调整。
                        </div>
                      }
                    >
                      <QuestionCircleOutlined style={{ color: '#666', cursor: 'help' }} />
                    </Tooltip>
                  </Text>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 4, marginTop: 4 }}>
                    <InputNumber
                      size="small" addonBefore="幅度" min={0.01} max={10} step={0.01} style={{ width: '100%' }}
                      value={dragScale}
                      onChange={(v) => useEditorUiStore.getState().setDragScale(v ?? 1)}
                    />
                    <InputNumber
                      size="small" addonBefore="移动" min={0} step={0.5} style={{ width: '100%' }}
                      value={moveSnap}
                      onChange={(v) => useEditorUiStore.getState().setMoveSnap(v ?? 0)}
                    />
                    <InputNumber
                      size="small" addonBefore="旋转" min={0} step={5} style={{ width: '100%' }}
                      value={rotateSnap}
                      onChange={(v) => useEditorUiStore.getState().setRotateSnap(v ?? 0)}
                    />
                  </div>
                </div>
                {/* 2026-09-14：悬停高亮开关（方案A 抑制组合——大模型占满屏幕自动不染色；可整体关闭） */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Switch
                    size="small"
                    checked={hoverHighlight}
                    onChange={(v) => {
                      useEditorUiStore.getState().setHoverHighlight(v);
                      if (!v) useHoverStore.getState().setHovered(null);
                    }}
                  />
                  <Text style={{ fontSize: 12, color: '#888' }}>悬停高亮</Text>
                  <Tooltip
                    title={
                      <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                        光标悬停模型时的浅蓝提示（不影响选中/告警高亮）。<br />
                        · <b>大模型占满屏幕时自动不染色</b>（避免全屏色差）；<br />
                        · 快速划过不触发（150ms 延迟）；<br />
                        · 旋转/平移相机、拖拽部件时不显示；<br />
                        · 设置保存在本机（刷新后保留）。
                      </div>
                    }
                  >
                    <QuestionCircleOutlined style={{ color: '#666', cursor: 'help' }} />
                  </Tooltip>
                </div>
                {/* 2026-09-14：选中同源部件（同共享材质——对齐 Blender「选择关联→材质」/ Unity「Select by Material」） */}
                {subSel && subSel.modelId === selectedModel.id && (
                  <div>
                    <Button
                      size="small"
                      block
                      icon={<ClusterOutlined />}
                      disabled={sameMaterialUuids.length <= 1}
                      title="选中模型中所有与当前部件共享同一材质的部件（多选后按 Delete 可批量删除，Ctrl+Z 撤销）"
                      onClick={() => {
                        useSceneStore.getState().selectSubObjects(
                          sameMaterialUuids.map((u) => ({ modelId: selectedModel.id, uuid: u })),
                        );
                        message.success(`已选中 ${sameMaterialUuids.length} 个同源部件（共享材质）`);
                      }}
                    >
                      选中同源部件（{sameMaterialUuids.length} 个）
                    </Button>
                  </div>
                )}

                {/* Position - units: meters */}
                <div>
                  <Text style={{ fontSize: 12, color: '#888' }}>位置 (m)</Text>
                  <div data-testid="position-axes" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 4 }}>
                    <InputNumber
                      size="small" addonBefore="X" style={{ width: '100%' }} value={selectedModel.position[0]} step={0.5}
                      onChange={(v: number | null) => debouncedUpdate(selectedModel.id, { position: [v ?? 0, selectedModel.position[1], selectedModel.position[2]] })}
                    />
                    <InputNumber
                      size="small" addonBefore="Y" style={{ width: '100%' }} value={selectedModel.position[1]} step={0.5}
                      onChange={(v: number | null) => debouncedUpdate(selectedModel.id, { position: [selectedModel.position[0], v ?? 0, selectedModel.position[2]] })}
                    />
                    <InputNumber
                      size="small" addonBefore="Z" style={{ width: '100%' }} value={selectedModel.position[2]} step={0.5}
                      onChange={(v: number | null) => debouncedUpdate(selectedModel.id, { position: [selectedModel.position[0], selectedModel.position[1], v ?? 0] })}
                    />
                  </div>
                </div>

                {/* GIS Coordinates */}
                {gisEnabled && (
                  <div>
                    <Text style={{ fontSize: 12, color: '#888' }}>地理坐标</Text>
                    <Space orientation="vertical" style={{ width: '100%' }}>
                      <InputNumber
                        size="small" addonBefore="经度" style={{ width: '100%' }}
                        value={selectedModel.longitude ?? 116.4}
                        onChange={(v) => debouncedUpdate(selectedModel.id, { longitude: v ?? 0 })}
                      />
                      <InputNumber
                        size="small" addonBefore="纬度" style={{ width: '100%' }}
                        value={selectedModel.latitude ?? 39.9}
                        onChange={(v) => debouncedUpdate(selectedModel.id, { latitude: v ?? 0 })}
                      />
                      <InputNumber
                        size="small" addonBefore="海拔" style={{ width: '100%' }}
                        value={selectedModel.altitude ?? 0}
                        onChange={(v) => debouncedUpdate(selectedModel.id, { altitude: v ?? 0 })}
                      />
                    </Space>
                  </div>
                )}

                {/* Rotation - Learning from Three.js editor: show degrees, store radians */}
                <div>
                  <Text style={{ fontSize: 12, color: '#888' }}>旋转 (°)</Text>
                  <div data-testid="rotation-axes" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 4 }}>
                    <InputNumber size="small" addonBefore="X" style={{ width: '100%' }} value={Math.round(toDegrees(selectedModel.rotation[0]) * 100) / 100} step={0.5}
                      onChange={(v: number | null) => debouncedUpdate(selectedModel.id, { rotation: [toRadians(v ?? 0), selectedModel.rotation[1], selectedModel.rotation[2]] })}
                    />
                    <InputNumber size="small" addonBefore="Y" style={{ width: '100%' }} value={Math.round(toDegrees(selectedModel.rotation[1]) * 100) / 100} step={0.5}
                      onChange={(v: number | null) => debouncedUpdate(selectedModel.id, { rotation: [selectedModel.rotation[0], toRadians(v ?? 0), selectedModel.rotation[2]] })}
                    />
                    <InputNumber size="small" addonBefore="Z" style={{ width: '100%' }} value={Math.round(toDegrees(selectedModel.rotation[2]) * 100) / 100} step={0.5}
                      onChange={(v: number | null) => debouncedUpdate(selectedModel.id, { rotation: [selectedModel.rotation[0], selectedModel.rotation[1], toRadians(v ?? 0)] })}
                    />
                  </div>
                </div>

                {/* Scale */}
                <div>
                  <Text style={{ fontSize: 12, color: '#888' }}>缩放</Text>
                  <div data-testid="scale-axes" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 4 }}>
                    <InputNumber size="small" addonBefore="X" style={{ width: '100%' }} value={selectedModel.scale[0]} step={0.1} min={0.001}
                      onChange={(v: number | null) => {
                        // 缩放输入中间态（如先输入 "0" 再补 ".6"）不得立即写入 scale=0：
                        // scale=0 会使矩阵奇异(NaN)导致 3D 视图黑屏。此处拦截 ≤0，等待完整输入
                        if (v == null || v <= 0) return;
                        logUpdate(selectedModel.id, { scale: [v, selectedModel.scale[1], selectedModel.scale[2]] });
                      }}
                    />
                    <InputNumber size="small" addonBefore="Y" style={{ width: '100%' }} value={selectedModel.scale[1]} step={0.1} min={0.001}
                      onChange={(v: number | null) => {
                        if (v == null || v <= 0) return;
                        logUpdate(selectedModel.id, { scale: [selectedModel.scale[0], v, selectedModel.scale[2]] });
                      }}
                    />
                    <InputNumber size="small" addonBefore="Z" style={{ width: '100%' }} value={selectedModel.scale[2]} step={0.1} min={0.001}
                      onChange={(v: number | null) => {
                        if (v == null || v <= 0) return;
                        logUpdate(selectedModel.id, { scale: [selectedModel.scale[0], selectedModel.scale[1], v] });
                      }}
                    />
                  </div>
                </div>

                {/* Visibility */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text style={{ fontSize: 12, color: '#888' }}>可见</Text>
                  <Switch
                    checked={selectedModel.visible}
                    onChange={(v) => updateModel(selectedModel.id, { visible: v })}
                  />
                </div>

                {/* 阶段4｜G3：设备联动（模型↔设备绑定，持久化；3D 点击联动+2D 设备表格驱动） */}
                <div>
                  <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>
                    设备联动 <Text type="secondary" style={{ fontSize: 11 }}>模型 ↔ 设备（保存后刷新可恢复）</Text>
                  </Text>
                  <DeviceLinkSelect model={selectedModel} />
                  <DeviceLabelToggle model={selectedModel} />
                </div>

                {/* 阶段5：数据绑定区（视觉规则：设备点位 → 颜色等） */}
                <VisualBindingPanel model={selectedModel} />

                <RuleOutputBindingPanel model={selectedModel} />

                <AnimationPanel handle={animHandle} />
              </Space>
            )
          },
          {
            key: 'geometry',
            label: '几何',
            children: <GeometryInfo model={selectedModel} />
          },
          {
            key: 'material',
            label: '材质',
            children: <MaterialEditor model={selectedModel} />
          }
        ]}
      />
    </div>
    </>
  );
}
